// Ordering keys for stops — the client half of `stops.sort_key`.
//
// The column is an opaque string ordered lexicographically, so a card can be
// placed between two neighbours by writing any string strictly between their
// keys: no renumbering, and two devices inserting at the same spot both survive
// (see roamoir-mvp 20260828000036_offline_sync_foundation.sql §3).
//
// Server-assigned keys are 12-digit zero-padded decimals with a 4096 stride, or
// such a key with '5's appended. Everything here stays in the digit alphabet so
// client keys interleave with those correctly.

const DIGITS = '0123456789';

/**
 * A key strictly between `a` and `b`. Either may be null for "no neighbour on
 * that side". Throws if a >= b, which means the caller's list is out of order.
 */
export function keyBetween(a: string | null, b: string | null): string {
  // A trailing '0' adds nothing to the order ('12' < '120' with nothing in
  // between), and the midpoint below assumes it is absent. Server keys like
  // 000000008192 can end in 0 at the integer level only, so stripping is safe.
  const lo = (a ?? '').replace(/0+$/, '');
  const hi = b === null ? null : b.replace(/0+$/, '');
  if (hi !== null && lo >= hi) {
    throw new Error(`keyBetween: ${a} is not before ${b}`);
  }
  return midpoint(lo, hi);
}

function midpoint(a: string, b: string | null): string {
  if (b !== null) {
    // Shared prefix: the answer shares it too.
    let n = 0;
    while ((a[n] ?? '0') === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const da = a ? DIGITS.indexOf(a[0]) : 0;
  const db = b !== null ? DIGITS.indexOf(b[0]) : DIGITS.length;
  if (db - da > 1) return DIGITS[Math.round((da + db) / 2)];
  // Adjacent digits: keep the lower one and go a level deeper.
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return DIGITS[da] + midpoint(a.slice(1), null);
}

/** Order stops the way the schema defines it: (sort_key, stop_id). */
export function bySortKey<T extends { sort_key: string | null; stop_id: string }>(x: T, y: T): number {
  const a = x.sort_key ?? '';
  const b = y.sort_key ?? '';
  if (a !== b) return a < b ? -1 : 1;
  return x.stop_id < y.stop_id ? -1 : x.stop_id > y.stop_id ? 1 : 0;
}
